import type { LinkProps } from 'next/link';
import type {
  AllHTMLAttributes,
  ButtonHTMLAttributes,
  CSSProperties,
  DOMAttributes,
  LinkHTMLAttributes,
  ReactNode,
  Ref,
} from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';

import type { MarginProps } from '@/components/Box';
import { Loader } from '@/components/Loader';
import type { WithChildren } from '@/lib/types';
import type { ButtonColorVariant } from '@/theme/types/components';

import LinkWithLocale from '../LinkWithLocale/LinkWithLocale';

import * as S from './LegacyButton.styles';
import { defaultProps, switchTypographyProps } from './LegacyButton.styles';

export type ButtonVariant = 'filled' | 'outlined' | 'link';

export type ButtonSize = 's' | 'm' | 'l';

export interface ButtonProps
  extends Pick<
      ButtonHTMLAttributes<HTMLElement>,
      | 'autoFocus'
      | 'disabled'
      | 'form'
      | 'formAction'
      | 'formEncType'
      | 'formMethod'
      | 'formNoValidate'
      | 'formTarget'
      | 'name'
      | 'role'
      | 'type'
      | 'value'
      | 'className'
    >,
    Pick<DOMAttributes<HTMLElement>, 'onClick' | 'onFocus' | 'onBlur'>,
    Pick<LinkHTMLAttributes<HTMLElement>, 'href' | 'rel'>,
    Pick<AllHTMLAttributes<HTMLElement>, 'target'>,
    MarginProps,
    WithChildren<false> {
  ariaLabel?: string;
  as?: Extract<keyof JSX.IntrinsicElements, 'span' | 'button' | 'a'>;
  beforeContent?: boolean;
  className?: string;
  colorVariant?: ButtonColorVariant;
  isBold?: boolean;
  isFluid?: boolean;
  isProcessing?: boolean;
  isUpperCase?: boolean;
  label?: ReactNode;
  leftContent?: ReactNode;
  linkProps?: LinkProps;
  openUrlInNewTab?: boolean;
  size?: ButtonSize;
  style?: CSSProperties;
  variant?: ButtonVariant;
  width?: string;
}

const BUTTON_LOADER_HEIGHT = 36;

export const LegacyButton = forwardRef(
  (props: ButtonProps, ref: Ref<HTMLElement>) => {
    const {
      ariaLabel,
      as,
      beforeContent,
      children,
      href,
      isBold = true,
      isUpperCase = true,
      label,
      leftContent,
      linkProps,
      onClick,
      size = 'm',
      type,
      width,
      ...rest
    } = props;
    const isLink = !!linkProps?.href;
    const isExternalLink = !!href;
    const canProceed = !rest.isProcessing && !rest.disabled;

    const buttonRef = useRef<HTMLDivElement>(null);
    const [buttonWidth, setButtonWidth] = useState<string | null>(null);

    useEffect(() => {
      if (buttonRef.current && !rest.isProcessing) {
        setButtonWidth(`${buttonRef.current.offsetWidth}px`);
      }
    }, [canProceed]);

    return isLink ? (
      <S.InternalLink
        {...(linkProps as any)}
        aria-label={ariaLabel}
        target={rest?.openUrlInNewTab ? '_blank' : '_self'}
      >
        <S.LinkButtonInnerContent
          ref={ref}
          beforeContent={beforeContent}
          forwardedAs='span'
          onClick={canProceed ? onClick : undefined}
          size={size}
          {...switchTypographyProps(size)}
          {...rest}
          constantWidth={width ?? buttonWidth}
          disabled={props.isProcessing ? false : props.disabled}
          isBold={isBold}
          isUpperCase={isUpperCase}
        >
          {rest.isProcessing ? (
            <Loader size='sm' variant='contrast' />
          ) : (
            <>
              {leftContent}
              {label}
              {children}
            </>
          )}
        </S.LinkButtonInnerContent>
      </S.InternalLink>
    ) : (
      <S.ButtonContainer
        ref={(node: HTMLElement | null) => {
          if (ref) {
            if (typeof ref === 'function') {
              ref(node as HTMLElement);
            } else if ('current' in ref) {
              (ref as React.MutableRefObject<HTMLElement>).current =
                node as HTMLElement;
            }
          }
          // @ts-ignore
          buttonRef.current = node as HTMLDivElement;
        }}
        aria-label={ariaLabel}
        beforeContent={beforeContent}
        forwardedAs={isExternalLink && !isLink ? LinkWithLocale : as}
        isUpperCase={isUpperCase}
        onClick={canProceed ? onClick : undefined}
        size={size}
        {...(isExternalLink ? { href } : {})} //NOSONAR
        {...switchTypographyProps(size)}
        {...rest}
        colorVariant={rest.colorVariant}
        constantWidth={width ?? buttonWidth}
        disabled={
          props.type !== 'submit' && props.isProcessing ? false : props.disabled
        }
        isBold={isBold}
        isDisabled={rest.disabled}
        isProcessing={rest.isProcessing}
        target={
          type !== 'button'
            ? rest?.openUrlInNewTab
              ? '_blank'
              : '_self'
            : undefined
        }
        variant={rest.variant}
      >
        {rest.isProcessing ? (
          <Loader height={BUTTON_LOADER_HEIGHT} size='sm' variant='contrast' />
        ) : (
          <>
            {leftContent}
            {label}
            {children}
          </>
        )}
      </S.ButtonContainer>
    );
  },
);

LegacyButton.defaultProps = defaultProps;

LegacyButton.displayName = 'LegacyButton';
